import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Building2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function OrgSettings() {
  const [depts, setDepts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dep, setDep] = useState({ name: "", code: "" });
  const [pos, setPos] = useState({ name: "", department_id: "", level: "" });

  const load = useCallback(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/positions").then((r) => setPositions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addDep = async () => {
    if (!dep.name) return toast.error("Nama departemen wajib diisi");
    try { await api.post("/departments", dep); toast.success("Departemen ditambahkan"); setDep({ name: "", code: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addPos = async () => {
    if (!pos.name) return toast.error("Nama jabatan wajib diisi");
    try { await api.post("/positions", pos); toast.success("Jabatan ditambahkan"); setPos({ name: "", department_id: "", level: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delDep = async (id) => { try { await api.delete(`/departments/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delPos = async (id) => { try { await api.delete(`/positions/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const depName = (id) => depts.find((d) => d.id === id)?.name || "-";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Departemen & Jabatan</h1>
        <p className="mt-1 text-sm text-slate-500">Kelola struktur organisasi perusahaan.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Departments */}
        <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800">
          <div className="mb-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Departemen</h3></div>
          <div className="flex gap-2">
            <Input data-testid="dept-name-input" placeholder="Nama departemen" value={dep.name} onChange={(e) => setDep({ ...dep, name: e.target.value })} />
            <Input data-testid="dept-code-input" placeholder="Kode" className="w-24" value={dep.code} onChange={(e) => setDep({ ...dep, code: e.target.value })} />
            <Button data-testid="add-dept-button" onClick={addDep}><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-4 space-y-2">
            {depts.map((d) => (
              <div key={d.id} data-testid="dept-item" className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-800/40">
                <span className="text-sm font-medium">{d.name} <span className="font-mono text-xs text-slate-400">{d.code}</span></span>
                <Button size="icon" variant="ghost" onClick={() => delDep(d.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
          </div>
        </Card>

        {/* Positions */}
        <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800">
          <div className="mb-4 flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Jabatan</h3></div>
          <div className="space-y-2">
            <Input data-testid="pos-name-input" placeholder="Nama jabatan" value={pos.name} onChange={(e) => setPos({ ...pos, name: e.target.value })} />
            <div className="flex gap-2">
              <Select value={pos.department_id} onValueChange={(v) => setPos({ ...pos, department_id: v })}>
                <SelectTrigger data-testid="pos-dept-select"><SelectValue placeholder="Departemen" /></SelectTrigger>
                <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Level" className="w-28" value={pos.level} onChange={(e) => setPos({ ...pos, level: e.target.value })} />
              <Button data-testid="add-pos-button" onClick={addPos}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {positions.map((p) => (
              <div key={p.id} data-testid="pos-item" className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-800/40">
                <span className="text-sm font-medium">{p.name} <span className="text-xs text-slate-400">· {depName(p.department_id)}</span></span>
                <Button size="icon" variant="ghost" onClick={() => delPos(p.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
